import { classnames } from '@aibees/shared/utils';
import { Box, Stack, Typography } from '@mui/material';
import { memo, useMemo } from 'react';
import { Dot } from '../dot';
import { Icon } from '../icon';
import { Label } from '../label';
import { useIsMiniLayout } from '../settings';
import { TextEllipsis } from '../text-ellipsis';
import * as Styled from './styles';
import * as SX from './sx';
import type { CustomAccordionProps as Props } from './types';

export const CustomAccordion = memo(
  ({
    className,
    title,
    labelCount,
    children,
    leftIcon,
    rightIcon,
    description,
    descriptionW,
    avatar,
    id,
    expandedAccordion,
    unmountOnExit = true,
    hasTag = true,
    setExpandedAccordion,
    sx,
  }: Props) => {
    const isMini = useIsMiniLayout();
    const isExpanded = expandedAccordion === id;

    const renderExpandIcon = !isMini && (
      <Icon
        size={20}
        color={isExpanded ? 'primary.500' : 'default'}
        name={rightIcon ?? 'chevron-down'}
      />
    );

    const handleChange = () => {
      setExpandedAccordion?.(isExpanded ? null : id || null);
    };

    const accordionSlotProps = useMemo(
      () => ({
        transition: {
          unmountOnExit,
        },
      }),
      [unmountOnExit]
    );

    return (
      <Styled.Wrapper
        className={classnames('CustomAccordion', className)}
        expanded={isExpanded}
        slotProps={accordionSlotProps}
        disableGutters
        elevation={0}
        square
        onChange={handleChange}
      >
        <Styled.Summary expandIcon={renderExpandIcon}>
          <Styled.ContentSummary>
            <>
              {expandedAccordion !== id && description && hasTag && (
                <Box sx={SX.dot}>
                  <Dot width={6} height={6} />
                </Box>
              )}

              {leftIcon && (
                <Stack>
                  <Icon size={18} name={leftIcon} color="grey.600" />
                </Stack>
              )}
              {avatar}
            </>

            <Styled.Title>
              {title && (
                <Typography variant="body3" fontWeight={600} color="grey.800">
                  {title}
                </Typography>
              )}

              {!isExpanded && description && (
                <Box sx={SX.descriptionWrap}>
                  <TextEllipsis
                    variant="body4"
                    color="grey.500"
                    w={descriptionW}
                  >
                    {description}
                  </TextEllipsis>
                </Box>
              )}
            </Styled.Title>

            {!!labelCount && (
              <Label variant="soft" color="primary" size="xxsmall">
                {labelCount}
              </Label>
            )}
          </Styled.ContentSummary>
        </Styled.Summary>
        <Styled.AccordionDetails>{children}</Styled.AccordionDetails>
      </Styled.Wrapper>
    );
  }
);

CustomAccordion.displayName = 'CustomAccordion';
